/**
 * useMultiLanguage Hook - Handles multi-language support for forms
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { Template } from '../../services/templateService';

export type Language = 'en' | 'de' | 'fr' | 'it';

export interface UseMultiLanguageOptions {
  template?: Template | null;
  defaultLanguage?: Language;
  fallbackLanguage?: Language;
}

export interface UseMultiLanguageReturn {
  currentLanguage: Language;
  availableLanguages: Language[];
  setLanguage: (language: Language) => void;
  translate: (key: string, defaultValue?: string) => string;
  translateField: (fieldCode: string, property: string, defaultValue?: string) => string;
  isRTL: boolean;
}

const LANGUAGE_NAMES: Record<Language, string> = {
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  it: 'Italiano',
};

const RTL_LANGUAGES: Language[] = []; // Add RTL languages if needed

export const useMultiLanguage = (options: UseMultiLanguageOptions = {}): UseMultiLanguageReturn => {
  const {
    template,
    defaultLanguage = 'en',
    fallbackLanguage = 'en',
  } = options;

  const [currentLanguage, setCurrentLanguage] = useState<Language>(defaultLanguage);

  // Get available languages from template translations
  const availableLanguages = useMemo((): Language[] => {
    if (!template?.translations) {
      return ['en'];
    }

    const languages = Object.keys(template.translations) as Language[];

    // Always include English as fallback
    if (!languages.includes('en')) {
      languages.unshift('en');
    }

    return languages;
  }, [template]);

  // Get translations for current language
  const currentTranslations = useMemo(() => {
    if (!template?.translations) {
      return {};
    }

    return template.translations[currentLanguage] || {};
  }, [template, currentLanguage]);

  // Get fallback translations
  const fallbackTranslations = useMemo(() => {
    if (!template?.translations) {
      return {};
    }

    return template.translations[fallbackLanguage] || {};
  }, [template, fallbackLanguage]);

  // Translate a key
  const translate = useCallback((key: string, defaultValue?: string): string => {
    // Try current language
    if (currentTranslations[key]) {
      return currentTranslations[key];
    }

    // Try fallback language
    if (fallbackTranslations[key]) {
      return fallbackTranslations[key];
    }

    // Return default value or key
    return defaultValue || key;
  }, [currentTranslations, fallbackTranslations]);

  // Translate field properties
  const translateField = useCallback((fieldCode: string, property: string, defaultValue?: string): string => {
    const key = `${fieldCode}.${property}`;
    return translate(key, defaultValue);
  }, [translate]);

  // Set language with validation
  const setLanguage = useCallback((language: Language) => {
    if (availableLanguages.includes(language)) {
      setCurrentLanguage(language);

      // Store in localStorage for persistence
      localStorage.setItem('preferredLanguage', language);
    } else {
      console.warn(`Language ${language} not available, falling back to ${fallbackLanguage}`);
      setCurrentLanguage(fallbackLanguage);
    }
  }, [availableLanguages, fallbackLanguage]);

  // Check if current language is RTL
  const isRTL = useMemo(() => {
    return RTL_LANGUAGES.includes(currentLanguage);
  }, [currentLanguage]);

  // Load preferred language from localStorage on mount
  useEffect(() => {
    const storedLanguage = localStorage.getItem('preferredLanguage') as Language;
    if (storedLanguage && availableLanguages.includes(storedLanguage)) {
      setCurrentLanguage(storedLanguage);
    }
  }, [availableLanguages]);

  return {
    currentLanguage,
    availableLanguages,
    setLanguage,
    translate,
    translateField,
    isRTL,
  };
};

// Language selector component helper
export interface LanguageSelectorProps {
  currentLanguage: Language;
  availableLanguages: Language[];
  onLanguageChange: (language: Language) => void;
  className?: string;
}

export const getLanguageName = (language: Language): string => {
  return LANGUAGE_NAMES[language] || language.toUpperCase();
};

export const getLanguageFlag = (language: Language): string => {
  const flags: Record<Language, string> = {
    en: '🇬🇧',
    de: '🇩🇪',
    fr: '🇫🇷',
    it: '🇮🇹',
  };
  return flags[language] || '🌐';
};

export default useMultiLanguage;